/**
 * PrisonGuardModal.tsx — R4 jail UX
 *
 * Dramatic modal for paying bail via the Prison Guard NPC on the Jail tile.
 * Replaces the old silent payToLeaveJail() call on E-press / click.
 *
 * Renders when prisonGuardModalOpen === true.
 *
 * VISIBLE WHEN: prisonGuardModalOpen === true (currentSeat in jail, Jail tile)
 * TEST PATH: land on Jail tile → walk to guard NPC → E / click → modal opens
 */

import type React from "react";
import { useEffect, useRef } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./PrisonGuardModal.module.css";

// ---------------------------------------------------------------------------
// Narrative variants — pre-interaction guard lines
// ---------------------------------------------------------------------------
const GUARD_LINES = [
  "Got tokens? Or staying another turn?",
  "Bail's the only door outta here, friend.",
  "I've seen tougher inmates pay up. Your call.",
  "Clock's tickin'. Pay or rot another round.",
  "No charity here. Tokens or you stay put.",
  "I'm a reasonable man. Reasonable price.",
];

function pickGuardLine(seed: number): string {
  return GUARD_LINES[seed % GUARD_LINES.length] ?? "Got tokens? Or staying another turn?";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PrisonGuardModal(): React.ReactElement | null {
  const { t } = useI18n();
  const open = useGameStore((s) => s.prisonGuardModalOpen);
  const closePrisonGuardModal = useGameStore((s) => s.closePrisonGuardModal);
  const payToLeaveJail = useGameStore((s) => s.payToLeaveJail);

  const currentSeat = useGameStore((s) => s.currentSeat);
  const tokens = useGameStore((s) => s.tokens);
  const bailMultiplier = useGameStore((s) => s.bailMultiplier);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const jailTurnsRemaining = useGameStore((s) => s.jailTurnsRemaining);
  const markNpcInteracted = useGameStore((s) => s.markNpcInteracted);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const getNpcInteractionKey = useGameStore((s) => s.getNpcInteractionKey);

  // Stable narrative variant chosen once per modal open via seat as seed
  const lineRef = useRef<string>("");
  const prevOpenRef = useRef(false);
  if (open && !prevOpenRef.current) {
    lineRef.current = pickGuardLine(currentSeat + (Date.now() % 97));
  }
  prevOpenRef.current = open;

  // Focus the dialog panel on open for accessibility
  const panelRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (open && panelRef.current) {
      panelRef.current.focus();
    }
  }, [open]);

  // Esc closes the modal (wait-it-out path)
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        closePrisonGuardModal();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closePrisonGuardModal]);

  if (!open) return null;

  const balance = tokens[currentSeat] ?? 0;
  // Mirror computeBailPrice from store: mean over PRESENT seats only — absent
  // seats >= numPlayers keep INITIAL_TOKENS=100 and would inflate it in 2/3p games.
  const presentCount = numPlayers ?? 4;
  const mean =
    tokens.slice(0, presentCount).reduce<number>((sum, t) => sum + (t ?? 0), 0) / presentCount;
  const mult = bailMultiplier[currentSeat] ?? 0.25;
  const bailPrice = Math.max(1, Math.round(mean * mult));
  const canAfford = balance >= bailPrice;
  const turnsLeft = jailTurnsRemaining[currentSeat] ?? 0;

  function handlePay(): void {
    payToLeaveJail();
    // NPC marked interacted (speech bubble switches to completed line)
    const tileIdx = playerTiles[currentSeat] ?? 0;
    const key = getNpcInteractionKey(tileIdx, "guard.png");
    void key; // markNpcInteracted handles its own key logic
    markNpcInteracted(tileIdx, "guard.png");
  }

  function handleWait(): void {
    closePrisonGuardModal();
  }

  return (
    <div
      className={styles.overlay}
      data-testid="prison-guard-modal-overlay"
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget) handleWait();
      }}
      onKeyDown={(e) => {
        // Mirror Esc / Enter on the backdrop for keyboard-only users
        if ((e.key === "Escape" || e.key === "Enter") && e.target === e.currentTarget) {
          handleWait();
        }
      }}
      role="presentation"
    >
      <dialog
        open
        className={styles.panel}
        aria-labelledby="guard-modal-title"
        aria-describedby="guard-modal-narrative"
        data-testid="prison-guard-modal"
        ref={panelRef}
        tabIndex={-1}
        onCancel={(e) => {
          e.preventDefault();
          handleWait();
        }}
      >
        <h2 id="guard-modal-title" className={styles.title}>
          {t("prisonGuard.title")}
        </h2>
        <p id="guard-modal-narrative" className={styles.narrative}>
          "{lineRef.current}"
        </p>

        <div className={styles.statusBox}>
          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>{t("prisonGuard.yourTokens")}</span>
            <span className={styles.statusValue}>{balance} tokens</span>
          </div>
          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>{t("prisonGuard.bailPrice")}</span>
            <span className={styles.statusValue}>{bailPrice} tokens</span>
          </div>
          <div className={styles.statusRow}>
            <span className={styles.statusLabel}>{t("prisonGuard.turnsRemaining")}</span>
            <span className={styles.statusValue}>{turnsLeft}</span>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.payBtn}
            onClick={handlePay}
            disabled={!canAfford}
            title={canAfford ? `Pay ${bailPrice} tokens to leave jail` : "Not enough tokens"}
            data-testid="guard-modal-pay"
          >
            Pay {bailPrice} tokens — leave jail
          </button>
          <button
            type="button"
            className={styles.waitBtn}
            onClick={handleWait}
            data-testid="guard-modal-wait"
          >
            Wait it out
          </button>
        </div>
      </dialog>
    </div>
  );
}
